import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";

const MONTHS = ["Januari", "Februari", "Mac", "April", "Mei", "Jun", "Julai", "Ogos", "September", "Oktober", "November", "Disember"];
const WEEKDAYS = ["Isn", "Sel", "Rab", "Kha", "Jum", "Sab", "Ahd"];
const STATUS_CLS = {
  menunggu: "bg-slate-100 text-slate-700 border-slate-300",
  menunggu_kelulusan: "bg-amber-100 text-amber-800 border-amber-300",
  diluluskan: "bg-green-50 text-[#146c43] border-green-300",
  ditolak: "bg-red-50 text-[#b02a37] border-red-200",
  selesai: "bg-indigo-50 text-[#10056E] border-indigo-200",
};

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function BookingCalendar({ bookings }) {
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const todayIso = iso(new Date());

  const weeks = useMemo(() => {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = new Date(first);
    start.setDate(first.getDate() - ((first.getDay() + 6) % 7));
    const out = [];
    const cur = new Date(start);
    for (let w = 0; w < 6; w++) {
      const row = [];
      for (let i = 0; i < 7; i++) {
        row.push(new Date(cur));
        cur.setDate(cur.getDate() + 1);
      }
      out.push(row);
      if (cur.getMonth() !== anchor.getMonth() && cur > first) break;
    }
    return out;
  }, [anchor]);

  const byDay = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      const s = (b.start_datetime || "").slice(0, 10);
      const e = (b.end_datetime || "").slice(0, 10) || s;
      if (!s) continue;
      const d = new Date(s + "T00:00");
      while (iso(d) <= e) {
        (map[iso(d)] = map[iso(d)] || []).push(b);
        d.setDate(d.getDate() + 1);
      }
    }
    return map;
  }, [bookings]);

  const shift = (n) => setAnchor((a) => new Date(a.getFullYear(), a.getMonth() + n, 1));

  return (
    <div data-testid="booking-calendar">
      <div className="px-4 py-3 border-b flex items-center justify-between">
        <p className="font-semibold" data-testid="calendar-month-label">{MONTHS[anchor.getMonth()]} {anchor.getFullYear()}</p>
        <div className="flex items-center gap-1.5">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" className="h-8" onClick={() => { const d = new Date(); d.setDate(1); setAnchor(d); }} data-testid="calendar-today-btn">Hari Ini</Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      <div className="grid grid-cols-7 border-b bg-slate-50/70">
        {WEEKDAYS.map((d) => <div key={d} className="px-2 py-2 text-xs font-semibold text-muted-foreground text-center">{d}</div>)}
      </div>
      {weeks.map((row, wi) => (
        <div key={wi} className="grid grid-cols-7 border-b last:border-b-0">
          {row.map((d) => {
            const key = iso(d);
            const inMonth = d.getMonth() === anchor.getMonth();
            const items = byDay[key] || [];
            return (
              <div key={key} className={`min-h-24 border-r last:border-r-0 p-1.5 ${inMonth ? "" : "bg-slate-50/60"}`} data-testid={`calendar-day-${key}`}>
                <p className={`text-xs font-semibold mb-1 ${key === todayIso
                  ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#10056E] text-white"
                  : inMonth ? "text-slate-700" : "text-slate-400"}`}>{d.getDate()}</p>
                <div className="space-y-1">
                  {items.slice(0, 3).map((b) => (
                    <button key={b.id + key} onClick={() => navigate(`/tempahan/${b.id}`)}
                      data-testid={`calendar-booking-${b.id}-${key}`}
                      className={`w-full text-left border rounded px-1.5 py-0.5 text-[10px] font-medium truncate hover:opacity-80 transition-opacity ${STATUS_CLS[b.status] || STATUS_CLS.menunggu}`}>
                      {b.vehicle_reg ? `${b.vehicle_reg} · ` : ""}{b.destination}
                    </button>
                  ))}
                  {items.length > 3 && <p className="text-[10px] text-muted-foreground px-1">+{items.length - 3} lagi</p>}
                </div>
              </div>
            );
          })}
        </div>
      ))}
      <div className="px-4 py-2.5 flex flex-wrap gap-3 text-[11px] text-muted-foreground border-t">
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-slate-300" /> Menunggu</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-green-400" /> Diluluskan</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-indigo-400" /> Selesai</span>
        <span className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-red-300" /> Ditolak</span>
      </div>
    </div>
  );
}
